// HTML5 video player engine with WebOS/Samsung TV compatibility
import { setIsBuffering, setGlobalStreamType, setIsPlaying, previewToNotShow } from "../state";

// LG webOS 3.x = Chrome 38, webOS 4.x = Chrome 53, webOS 5.x = Chrome 68, webOS 6.x = Chrome 79+
function getChromeMajorVersion() {
  const m = navigator.userAgent.match(/Chrome\/(\d+)/);
  return m ? parseInt(m[1], 10) : 100;
}
const CHROME_VER = getChromeMajorVersion();
// Older webOS (2018 = webOS 4.0 / Chrome 53) needs a longer initial play delay
const INITIAL_PLAY_DELAY_MS = CHROME_VER < 68 ? 2500 : 1500;

let playerInstance = null;
let reconnect_count = 0;
let stuckCount = 0;
let bufferingTimer = null;
let seekTimeoutTimer = null;
let reconnect_timer = null;

const MAX_RETRIES = 5;
let currentUrl = "";
let context = "";
let seeking = false;
let lastKnownTime = 0;
let monitorTimer = null;
let isBuffering = false;
let playerReady = false;
let playerState = "IDLE";
// Possible states: IDLE | LOADING | READY | PLAYING | PAUSED | SEEKING | BUFFERING | RECONNECTING | COMPLETED | ERROR

let lastSeekTime = 0;
const SEEK_THROTTLE_MS = 800;
const SEEK_TIMEOUT_MS = 10000;
const STUCK_THRESHOLD_MS = 100;
const MAX_STUCK_COUNT = 3;

let lastSuccessfulSeekTime = 0;
let seekingStartTime = 0;
let consecutiveStuckCount = 0;

if (!window.reconnectInfo) {
  window.reconnectInfo = { count: reconnect_count, max: MAX_RETRIES };
}

const videoPlayer = {

  destroy() {
    try {
      stopMonitoring();
      playerReady = false;
      playerState = "IDLE";
      seeking = false;

      clearTimeout(seekTimeoutTimer);
      clearTimeout(bufferingTimer);

      if (playerInstance) {
        try {
          playerInstance.pause();
          playerInstance.removeAttribute("src");
          playerInstance.load();
        } catch (e) {
          console.warn("Stop failed during destroy:", e);
        }
      }

      reconnect_count = 0;
      consecutiveStuckCount = 0;
      window.reconnectInfo.count = 0;
      clearTimeout(reconnect_timer);
      setIsBuffering(false);
      setIsPlaying(false);

      if (playerInstance && playerInstance.parentNode) {
        playerInstance.parentNode.removeChild(playerInstance);
        playerInstance = null;
      }
    } catch (e) {
      console.error("Error destroying player:", e);
    }
  },

  playVideo(streamUrl, playerContext, isRTLMode = false) {
    this.isRTLMode = isRTLMode;

    if (currentUrl === streamUrl && playerInstance && playerReady && playerState === "PLAYING") {
      return Promise.resolve();
    }

    currentUrl = streamUrl;
    context = playerContext;
    reconnect_count = 0;
    consecutiveStuckCount = 0;
    window.reconnectInfo.count = 0;
    playerReady = false;
    playerState = "IDLE";
    seeking = false;
    clearTimeout(reconnect_timer);
    clearTimeout(seekTimeoutTimer);

    return new Promise((resolve, reject) => {
      try {
        this.destroy();

        const videoElem = document.createElement("video");
        // Do NOT set crossorigin="anonymous": IPTV servers don't send CORS headers,
        // and Chrome 53 (webOS 4.x / LG 2018) silently blocks streams that fail CORS.
        videoElem.setAttribute("playsinline", "true");
        videoElem.setAttribute("webkit-playsinline", "true");
        videoElem.setAttribute("x-webkit-airplay", "allow");
        videoElem.autoplay = false;
        videoElem.muted = false;
        videoElem.controls = false;
        videoElem.preload = "metadata";

        videoElem.style.position = "absolute";
        if (context === "preview") {
          videoElem.style.left = isRTLMode ? "1%" : "60.5%";
          videoElem.style.transform = "translate(0%, -92%)";
        } else {
          videoElem.style.left = "0";
          videoElem.style.transform = "";
        }
        videoElem.style.top = context === "preview" ? "47%" : "0";
        videoElem.style.width = context === "preview" ? "35vw" : "100%";
        videoElem.style.height = context === "preview" ? "33vh" : "100%";
        videoElem.style.zIndex = context === "preview" ? "1" : "-1";
        videoElem.style.objectFit = "contain";

        document.body.appendChild(videoElem);
        playerInstance = videoElem;

        if (context === "preview") this.setPreviewScreen(isRTLMode);
        else if (context === "background") this.setFullScreen();

        isBuffering = true;
        setIsBuffering(true);
        playerState = "LOADING";

        videoElem.addEventListener("loadstart", () => {
          console.log("🔄 loadstart");
          setIsBuffering(true);
          isBuffering = true;
        });

        videoElem.addEventListener("loadedmetadata", () => {
          console.log("✅ loadedmetadata — seekable:", videoElem.seekable.length > 0
            ? `${videoElem.seekable.start(0)}s – ${videoElem.seekable.end(0)}s` : "none");
          playerReady = true;
          playerState = "READY";
        });

        videoElem.addEventListener("loadeddata", () => {
          console.log("✅ loadeddata");
        });

        videoElem.addEventListener("canplay", () => {
          console.log("✅ canplay");
          setIsBuffering(false);
          isBuffering = false;
          reconnect_count = 0;
          consecutiveStuckCount = 0;
          window.reconnectInfo.count = 0;
        });

        videoElem.addEventListener("canplaythrough", () => {
          console.log("✅ canplaythrough");
          setIsBuffering(false);
          isBuffering = false;
        });

        videoElem.addEventListener("playing", () => {
          console.log("▶ playing");
          playerState = "PLAYING";
          setIsPlaying(true);
          setGlobalStreamType("live");
          setIsBuffering(false);
          isBuffering = false;
          // Delay monitoring start — gives WebOS time to stabilize
          setTimeout(() => startMonitoring(), 3000);
        });

        videoElem.addEventListener("pause", () => {
          console.log("⏸ pause event");
          playerState = "PAUSED";
          setIsPlaying(false);
        });

        videoElem.addEventListener("waiting", () => {
          console.log("⏳ waiting (buffering check in 1200ms)");
          clearTimeout(bufferingTimer);
          // Confirm buffering only after 1200ms to avoid false positives on WebOS
          bufferingTimer = setTimeout(() => {
            if (!videoElem.paused && videoElem.readyState < 3) {
              console.log("🔄 buffering confirmed — readyState:", videoElem.readyState);
              setIsBuffering(true);
              isBuffering = true;
            }
          }, 1200);
        });

        videoElem.addEventListener("stalled", () => {
          if (!videoElem.paused && videoElem.readyState >= 3) {
            console.log("⚠️ stalled event ignored (false positive)");
            return;
          }
          console.log("⚠️ stalled — readyState:", videoElem.readyState);
          setIsBuffering(true);
          isBuffering = true;
        });

        videoElem.addEventListener("suspend", () => {
          console.log("⏸ suspend");
        });

        videoElem.addEventListener("ended", () => {
          console.log("🏁 ended");
          playerState = "COMPLETED";
          playerReady = false;
        });

        videoElem.addEventListener("seeking", () => {
          console.log("🔍 seeking started");
          seeking = true;
          playerState = "SEEKING";
          seekingStartTime = Date.now();
          clearTimeout(seekTimeoutTimer);
          seekTimeoutTimer = setTimeout(() => {
            if (seeking) {
              console.log("⚠️ seek timeout — forcing state to PLAYING");
              seeking = false;
              playerState = "PLAYING";
            }
          }, SEEK_TIMEOUT_MS);
        });

        videoElem.addEventListener("seeked", () => {
          const ms = Date.now() - seekingStartTime;
          console.log(`✅ seeked in ${ms}ms → ${Math.floor(videoElem.currentTime)}s`);
          seeking = false;
          playerState = "PLAYING";
          clearTimeout(seekTimeoutTimer);
          lastSuccessfulSeekTime = videoElem.currentTime * 1000;
          consecutiveStuckCount = 0;
        });

        videoElem.addEventListener("error", event => {
          playerReady = false;
          playerState = "ERROR";
          seeking = false;
          stopMonitoring();

          const error = event.target.error;
          let errorMsg = "Unknown error";
          if (error) {
            switch (error.code) {
              case error.MEDIA_ERR_ABORTED: errorMsg = "Media loading aborted"; break;
              case error.MEDIA_ERR_DECODE: errorMsg = "Media decode error"; break;
              case error.MEDIA_ERR_SRC_NOT_SUPPORTED: errorMsg = "Media source not supported"; break;
              case error.MEDIA_ERR_NETWORK:
                errorMsg = "Network error";
                setTimeout(() => tryReconnectMain(), 2000);
                return;
            }
          }
          tryReconnectMain();
          reject(`Player error: ${errorMsg}`);
        });

        videoElem.src = streamUrl;
        videoElem.load();

        const playWhenReady = () => {
          if (playerInstance && playerReady) {
            playerInstance.play()
              .then(() => resolve())
              .catch(error => {
                setTimeout(() => tryReconnectMain(), 3000);
                reject(error);
              });
          } else {
            setTimeout(playWhenReady, 200);
          }
        };
        // Wait before first play attempt — longer on older webOS (2018 = Chrome 53)
        setTimeout(playWhenReady, INITIAL_PLAY_DELAY_MS);

      } catch (e) {
        playerReady = false;
        playerState = "ERROR";
        tryReconnectMain();
        reject(e);
      }
    });
  },

  pause() {
    try {
      if (playerInstance && playerState === "PLAYING" && !seeking) {
        playerInstance.pause();
        playerState = "PAUSED";
        setIsPlaying(false);
      }
    } catch (e) { console.error("Error pausing:", e); }
  },

  resume() {
    try {
      if (playerInstance && playerState === "PAUSED" && !seeking) {
        playerInstance.play();
        playerState = "PLAYING";
        setIsPlaying(true);
      }
    } catch (e) { console.error("Error resuming:", e); }
  },

  seek(position) {
    return new Promise((resolve, reject) => {
      if (!playerInstance) { reject(new Error("Player not initialized")); return; }
      if (playerState !== "PLAYING" && playerState !== "PAUSED") {
        reject(new Error(`Cannot seek — state: ${playerState}`)); return;
      }
      if (seeking) { reject(new Error("Seek already in progress")); return; }
      if (playerInstance.seekable.length === 0) { reject(new Error("Not seekable")); return; }

      const now = Date.now();
      if (now - lastSeekTime < SEEK_THROTTLE_MS) { reject(new Error("Seek throttled")); return; }

      try {
        const duration = this.getDuration();
        const seekableStart = playerInstance.seekable.start(0) * 1000;
        const seekableEnd = playerInstance.seekable.end(0) * 1000;

        const clamped = Math.max(
          seekableStart,
          Math.min(position, Math.min(seekableEnd - 2000, duration - 2000))
        );

        if (!isFinite(clamped / 1000)) { reject(new Error("Invalid seek position")); return; }

        seeking = true;
        playerState = "SEEKING";
        lastSeekTime = now;
        seekingStartTime = now;

        const onSeeked = () => {
          playerInstance.removeEventListener("seeked", onSeeked);
          clearTimeout(seekTimeoutTimer);
          const actualTime = playerInstance.currentTime * 1000;
          seeking = false;
          playerState = "PLAYING";
          lastKnownTime = actualTime;
          lastSuccessfulSeekTime = actualTime;
          consecutiveStuckCount = 0;
          setTimeout(() => resolve(actualTime), 100);
        };

        playerInstance.addEventListener("seeked", onSeeked);

        // Timeout with graceful recovery — resolve with current position
        seekTimeoutTimer = setTimeout(() => {
          if (seeking) {
            playerInstance.removeEventListener("seeked", onSeeked);
            seeking = false;
            playerState = "PLAYING";
            try {
              const recovered = playerInstance.currentTime * 1000;
              lastKnownTime = recovered;
              resolve(recovered);
            } catch { reject(new Error("Seek timeout")); }
          }
        }, SEEK_TIMEOUT_MS);

        playerInstance.currentTime = clamped / 1000;

      } catch (e) {
        seeking = false;
        playerState = "PLAYING";
        reject(e);
      }
    });
  },

  getDuration() {
    if (!playerInstance || playerState === "IDLE" || playerState === "ERROR") {
      throw new Error("Player not ready — state: " + playerState);
    }
    const duration = playerInstance.duration * 1000;
    if (!isFinite(duration) || duration <= 0) throw new Error("Invalid duration: " + duration);
    return duration;
  },

  getCurrentTime() {
    if (!playerInstance || playerState === "IDLE" || playerState === "ERROR") {
      throw new Error("Player not ready — state: " + playerState);
    }
    const time = playerInstance.currentTime * 1000;
    if (!isFinite(time) || time < 0) throw new Error("Invalid currentTime: " + time);
    return time;
  },

  toggleAudio() { if (playerInstance) { playerInstance.muted = !playerInstance.muted; return playerInstance.muted; } },
  muteAudio() { if (playerInstance) { playerInstance.muted = true; return true; } return false; },
  unmuteAudio() { if (playerInstance) { playerInstance.muted = false; return true; } return false; },
  isMuted() { return playerInstance ? playerInstance.muted : false; },
  setMuted(m) { if (playerInstance) { playerInstance.muted = m; return true; } return false; },
  setVolume(level) { if (playerInstance) playerInstance.volume = Math.max(0, Math.min(1, level)); },
  getVolume() { return playerInstance ? playerInstance.volume : 0; },

  getAudioTracks() {
    if (!playerInstance) throw new Error("Player not initialized");
    try {
      const at = playerInstance.audioTracks;
      const tracks = [];
      for (let i = 0; i < at.length; i++) {
        tracks.push({ id: at[i].id, kind: at[i].kind, label: at[i].label, language: at[i].language, enabled: at[i].enabled });
      }
      return tracks;
    } catch (e) { console.error("getAudioTracks:", e); return []; }
  },

  setAudioTrack(trackIndex) {
    if (!playerInstance?.audioTracks) throw new Error("Audio tracks not available");
    try {
      for (let i = 0; i < playerInstance.audioTracks.length; i++) {
        playerInstance.audioTracks[i].enabled = (i === trackIndex);
      }
    } catch (e) { console.error("setAudioTrack:", e); }
  },

  getTextTracks() {
    if (!playerInstance) throw new Error("Player not initialized");
    try {
      const tt = playerInstance.textTracks;
      const tracks = [];
      for (let i = 0; i < tt.length; i++) {
        tracks.push({ id: tt[i].id, kind: tt[i].kind, label: tt[i].label, language: tt[i].language, mode: tt[i].mode });
      }
      return tracks;
    } catch (e) { console.error("getTextTracks:", e); return []; }
  },

  setTextTrack(trackIndex) {
    if (!playerInstance?.textTracks) throw new Error("Text tracks not available");
    try {
      for (let i = 0; i < playerInstance.textTracks.length; i++) {
        playerInstance.textTracks[i].mode = "disabled";
      }
      if (trackIndex >= 0 && trackIndex < playerInstance.textTracks.length) {
        playerInstance.textTracks[trackIndex].mode = "showing";
      }
    } catch (e) { console.error("setTextTrack:", e); }
  },

  disableAllSubtitles() {
    if (!playerInstance?.textTracks) return;
    try {
      for (let i = 0; i < playerInstance.textTracks.length; i++) {
        playerInstance.textTracks[i].mode = "disabled";
      }
    } catch (e) { console.error("disableAllSubtitles:", e); }
  },

  setFullScreen() {
    if (!previewToNotShow() && playerInstance) {
      try {
        Object.assign(playerInstance.style, {
          position: "absolute", left: "0", top: "0",
          width: "100%", height: "100%", zIndex: "-1", transform: ""
        });
      } catch (e) { console.error("setFullScreen:", e); }
    }
  },

  setPreviewScreen(isRTLMode = false) {
    if (previewToNotShow() && playerInstance) {
      try {
        Object.assign(playerInstance.style, {
          zIndex: "1",
          left: isRTLMode ? "1%" : "60.5%",
          right: "auto",
          top: "47%",
          width: "35vw",
          height: "33vh",
          position: "absolute",
          transform: "translate(0%, -92%)"
        });
      } catch (e) { console.error("setPreviewScreen:", e); }
    }
  },

  isReady() {
    return playerReady && !seeking && !isBuffering &&
      (playerState === "PLAYING" || playerState === "PAUSED");
  },

  getState() {
    return {
      ready: playerReady,
      seeking,
      buffering: isBuffering,
      state: playerState,
      consecutiveStuckCount,
      lastSuccessfulSeekTime,
    };
  },
};

function startMonitoring() {
  clearInterval(monitorTimer);
  monitorTimer = setInterval(() => {
    if (isBuffering || seeking || playerState !== "PLAYING" || !playerInstance) return;

    try {
      const currentTime = playerInstance.currentTime * 1000;
      const diff = Math.abs(currentTime - lastKnownTime);

      if (diff >= STUCK_THRESHOLD_MS) {
        lastKnownTime = currentTime;
        consecutiveStuckCount = 0;
      } else {
        consecutiveStuckCount++;
        if (consecutiveStuckCount >= MAX_STUCK_COUNT) {
          const timeSinceLastSeek = Date.now() - lastSeekTime;
          if (timeSinceLastSeek > 5000) {
            tryReconnectMain();
          } else {
            consecutiveStuckCount = Math.max(0, consecutiveStuckCount - 1);
          }
        }
      }
    } catch (e) {
      if (!e.message.includes("not ready")) console.error("Monitor error:", e.message);
    }
  }, 4000); // 4s interval keeps WebOS stable
}

function stopMonitoring() {
  if (monitorTimer) { clearInterval(monitorTimer); monitorTimer = null; }
}

function tryReconnectMain() {
  clearTimeout(reconnect_timer);
  reconnect_count++;
  window.reconnectInfo.count = reconnect_count;
  playerReady = false;
  playerState = "RECONNECTING";
  seeking = false;
  consecutiveStuckCount = 0;

  if (reconnect_count > MAX_RETRIES) {
    setIsBuffering(false);
    playerState = "ERROR";
    return;
  }

  setIsBuffering(true);
  // Exponential backoff: 1s, 2s, 4s, 8s, 8s (capped)
  const delay = Math.min(1000 * Math.pow(2, reconnect_count - 1), 8000);

  reconnect_timer = setTimeout(() => {
    try {
      stopMonitoring();

      if (playerInstance) {
        try {
          playerInstance.pause();
          playerInstance.currentTime = 0;
          playerInstance.load();
        } catch (e) { console.warn("Reconnect cleanup error:", e); }
      }

      if (currentUrl) {
        playerInstance.removeAttribute("src");
        playerInstance.load();

        setTimeout(() => {
          playerInstance.src = currentUrl;
          playerInstance.load();

          setTimeout(() => {
            playerInstance.play()
              .then(() => {
                setIsBuffering(false);
                setIsPlaying(true);
                playerReady = true;
                playerState = "PLAYING";
                reconnect_count = 0;
                consecutiveStuckCount = 0;
                window.reconnectInfo.count = 0;

                if (context === "preview") videoPlayer.setPreviewScreen(videoPlayer.isRTLMode || false);
                else videoPlayer.setFullScreen();

                setTimeout(() => startMonitoring(), 3000);
              })
              .catch(() => setTimeout(() => tryReconnectMain(), 3000));
          }, 1000);
        }, 800);
      }
    } catch (e) {
      console.error("Reconnection error:", e);
      setTimeout(() => tryReconnectMain(), 3000);
    }
  }, delay);
}

export default videoPlayer;
